import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  DriveFileMove as MoveIcon,
  ArrowForward as ArrowIcon,
  TableChart as TableIcon,
  Link as LinkIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DataTable as DataTableType, TableColumn, ColumnType } from '../types/table.types';
import { TableData } from '../types/tableData.types';
import { TableService } from '../services/table.service';
import { TableDataService, CopyToTableRequest } from '../services/tableData.service';
import dayjs from 'dayjs';

interface FieldConfig {
  mode: 'map' | 'custom' | 'empty';
  mappedField?: string;
  customValue?: any;
}

interface CopyToTableDialogProps {
  open: boolean;
  onClose: () => void;
  sourceTable: DataTableType;
  sourceData: TableData;
  onSuccess: () => void;
}

export default function CopyToTableDialog({
  open,
  onClose,
  sourceTable,
  sourceData,
  onSuccess,
}: CopyToTableDialogProps) {
  const [tables, setTables] = useState<DataTableType[]>([]);
  const [targetTableId, setTargetTableId] = useState('');
  const [targetTable, setTargetTable] = useState<DataTableType | null>(null);
  const [fieldConfigs, setFieldConfigs] = useState<Record<string, FieldConfig>>({});
  const [deleteFromSource, setDeleteFromSource] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchTables();
    }
  }, [open]);

  useEffect(() => {
    if (targetTableId) {
      fetchTargetTable(targetTableId);
    } else {
      setTargetTable(null);
      setFieldConfigs({});
    }
  }, [targetTableId]);

  // Автоматический маппинг полей с одинаковыми именами
  useEffect(() => {
    if (targetTable && sourceTable) {
      const autoConfigs: Record<string, FieldConfig> = {};

      targetTable.columns.forEach(targetCol => {
        const matchingSource = sourceTable.columns.find(
          sourceCol => sourceCol.name.toLowerCase() === targetCol.name.toLowerCase()
        );

        if (matchingSource && sourceData.data[matchingSource.name] !== undefined) {
          autoConfigs[targetCol.name] = {
            mode: 'map',
            mappedField: matchingSource.name,
          };
        } else {
          autoConfigs[targetCol.name] = {
            mode: 'empty',
          };
        }
      });

      setFieldConfigs(autoConfigs);
    }
  }, [targetTable, sourceTable, sourceData]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const data = await TableService.getAllTables();
      // Исключаем текущую таблицу из списка
      setTables(data.filter(t => t.id !== sourceTable.id));
    } catch (err: any) {
      setError('Ошибка загрузки таблиц');
    } finally {
      setLoading(false);
    }
  };

  const fetchTargetTable = async (tableId: string) => {
    try {
      const data = await TableService.getTableById(tableId);
      setTargetTable(data);
    } catch (err: any) {
      setError('Ошибка загрузки целевой таблицы');
    }
  };

  const handleModeChange = (targetField: string, mode: 'map' | 'custom' | 'empty') => {
    setFieldConfigs(prev => ({
      ...prev,
      [targetField]: {
        ...prev[targetField],
        mode,
        // Сбрасываем значения при смене режима
        mappedField: mode === 'map' ? prev[targetField]?.mappedField : undefined,
        customValue: mode === 'custom' ? prev[targetField]?.customValue : undefined,
      },
    }));
  };

  const handleMappedFieldChange = (targetField: string, sourceField: string) => {
    setFieldConfigs(prev => ({
      ...prev,
      [targetField]: {
        ...prev[targetField],
        mode: 'map',
        mappedField: sourceField || undefined,
      },
    }));
  };

  const handleCustomValueChange = (targetField: string, value: any) => {
    setFieldConfigs(prev => ({
      ...prev,
      [targetField]: {
        ...prev[targetField],
        mode: 'custom',
        customValue: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!targetTableId) {
      setError('Выберите целевую таблицу');
      return;
    }

    // Формируем данные для отправки
    const fieldMapping: Record<string, string> = {};
    const customData: Record<string, any> = {};

    Object.entries(fieldConfigs).forEach(([targetField, config]) => {
      if (config.mode === 'map' && config.mappedField) {
        fieldMapping[config.mappedField] = targetField;
      } else if (config.mode === 'custom' && config.customValue !== undefined && config.customValue !== '') {
        customData[targetField] = config.customValue;
      }
    });

    if (Object.keys(fieldMapping).length === 0 && Object.keys(customData).length === 0) {
      setError('Заполните хотя бы одно поле');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const request: CopyToTableRequest = {
        targetTableId,
        fieldMapping,
        customData,
        deleteFromSource,
      };

      await TableDataService.copyToTable(sourceTable.id, sourceData.id, request);

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка копирования данных');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTargetTableId('');
    setTargetTable(null);
    setFieldConfigs({});
    setDeleteFromSource(false);
    setError(null);
    onClose();
  };

  const renderFieldInput = (column: TableColumn, config: FieldConfig) => {
    if (config.mode === 'map') {
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={config.mappedField || ''}
          onChange={(e) => handleMappedFieldChange(column.name, e.target.value)}
          label="Исходное поле"
        >
          <MenuItem value="">
            <em>Не выбрано</em>
          </MenuItem>
          {sourceTable.columns.map((sourceCol) => (
            <MenuItem key={sourceCol.id} value={sourceCol.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {sourceCol.name}
                <Typography variant="caption" color="text.secondary">
                  ({sourceData.data[sourceCol.name] ?? 'пусто'})
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (config.mode === 'custom') {
      return renderCustomInput(column, config.customValue, (value) => handleCustomValueChange(column.name, value));
    }

    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Поле не будет заполнено
      </Typography>
    );
  };

  const renderCustomInput = (
    column: TableColumn,
    value: any,
    onChange: (value: any) => void
  ) => {
    switch (column.type) {
      case ColumnType.TEXT:
        return (
          <TextField
            size="small"
            fullWidth
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label="Значение"
            placeholder="Введите текст"
          />
        );

      case ColumnType.NUMBER:
        return (
          <TextField
            size="small"
            fullWidth
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            label="Значение"
            placeholder="Введите число"
          />
        );

      case ColumnType.DATE:
        return (
          <TextField
            size="small"
            fullWidth
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label="Значение"
            InputLabelProps={{ shrink: true }}
          />
        );

      case ColumnType.BOOLEAN:
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value === true}
                onChange={(e) => onChange(e.target.checked)}
              />
            }
            label="Да"
          />
        );

      case ColumnType.DROPDOWN:
        const options = column.options?.values || [];
        return (
          <TextField
            select
            size="small"
            fullWidth
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label="Значение"
          >
            <MenuItem value="">
              <em>Не выбрано</em>
            </MenuItem>
            {options.map((option: string) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      default:
        return (
          <TextField
            size="small"
            fullWidth
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label="Значение"
          />
        );
    }
  };

  const getFilledFieldsCount = () => {
    return Object.values(fieldConfigs).filter(
      config => 
        (config.mode === 'map' && config.mappedField) ||
        (config.mode === 'custom' && config.customValue !== undefined && config.customValue !== '')
    ).length;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          background: deleteFromSource
            ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {deleteFromSource ? <MoveIcon /> : <CopyIcon />}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {deleteFromSource ? 'Переместить в другую таблицу' : 'Копировать в другую таблицу'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Исходная таблица */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(79, 172, 254, 0.08)', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Исходная таблица
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#4facfe', width: 32, height: 32 }}>
              <TableIcon fontSize="small" />
            </Avatar>
            <Typography variant="body1" fontWeight={600}>
              {sourceTable.name}
            </Typography>
            <Chip
              label={`ID: ${sourceData.id.substring(0, 8)}...`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Выбор целевой таблицы */}
        <TextField
          select
          label="Целевая таблица"
          fullWidth
          value={targetTableId}
          onChange={(e) => setTargetTableId(e.target.value)}
          disabled={loading}
          sx={{ mb: 3 }}
        >
          {tables.length === 0 ? (
            <MenuItem disabled>Нет доступных таблиц</MenuItem>
          ) : (
            tables.map((table) => (
              <MenuItem key={table.id} value={table.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableIcon fontSize="small" color="primary" />
                  {table.name}
                  <Chip
                    label={`${table.columns.length} полей`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </MenuItem>
            ))
          )}
        </TextField>

        {/* Маппинг полей */}
        {targetTable && (
          <>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Настройка полей
              </Typography>
              <Chip
                label={`Заполнено: ${getFilledFieldsCount()} из ${targetTable.columns.length}`}
                size="small"
                color={getFilledFieldsCount() > 0 ? 'success' : 'default'}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Для каждого поля выберите: связать с исходным полем, ввести своё значение или оставить пустым
            </Typography>

            <Box sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
              {targetTable.columns.map((column) => {
                const config = fieldConfigs[column.name] || { mode: 'empty' };
                const isRequired = column.isRequired;

                return (
                  <Box
                    key={column.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: config.mode !== 'empty' 
                        ? 'rgba(76, 175, 80, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: config.mode !== 'empty' 
                        ? 'rgba(76, 175, 80, 0.3)' 
                        : 'divider',
                      transition: 'all 0.2s',
                    }}
                  >
                    {/* Заголовок поля */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {column.name}
                        </Typography>
                        <Chip
                          label={column.type}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        {isRequired && (
                          <Chip
                            label="Обязательное"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Переключатель режима */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <ToggleButtonGroup
                        value={config.mode}
                        exclusive
                        onChange={(_, newMode) => newMode && handleModeChange(column.name, newMode)}
                        size="small"
                      >
                        <ToggleButton value="map" sx={{ px: 2 }}>
                          <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
                          Из исходной
                        </ToggleButton>
                        <ToggleButton value="custom" sx={{ px: 2 }}>
                          <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                          Своё значение
                        </ToggleButton>
                        <ToggleButton value="empty" sx={{ px: 2 }}>
                          Пусто
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Поле ввода */}
                    {renderFieldInput(column, config)}
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Опция перемещения */}
            <FormControlLabel
              control={
                <Switch
                  checked={deleteFromSource}
                  onChange={(e) => setDeleteFromSource(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Удалить из исходной таблицы (переместить)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Запись будет удалена из "{sourceTable.name}" после копирования
                  </Typography>
                </Box>
              }
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !targetTableId || getFilledFieldsCount() === 0}
          startIcon={
            submitting ? (
              <CircularProgress size={20} />
            ) : deleteFromSource ? (
              <MoveIcon />
            ) : (
              <CopyIcon />
            )
          }
          sx={{
            background: deleteFromSource
              ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
              : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          }}
        >
          {submitting
            ? 'Обработка...'
            : deleteFromSource
            ? 'Переместить'
            : 'Копировать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}